import { useEffect, useRef, useState } from 'react';
import type { Dispatch } from 'react';
import { sanitizeConfig, type Action, type AppConfig } from '@ce/core';

/**
 * Preview mode: the app running inside the admin's iframe.
 *
 * The admin does not render its own approximation of the app. It loads the real
 * app and sends it the draft config, so what the editor shows is exactly what an
 * attendee would get — same components, same theme, same animation. The cost is
 * this small protocol, and the discipline that the app must accept a config from
 * a message the way it accepts one from the network: sanitised, never trusted.
 *
 * It is opt-in (`?preview=1`) and origin-checked. Outside preview the app ignores
 * every message, so a page that embeds it cannot restyle it.
 */

export const PREVIEW =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === '1';

const EXTRA_ORIGINS = String(import.meta.env.VITE_ADMIN_ORIGINS ?? 'http://localhost:5174,http://127.0.0.1:5174')
  .split(',')
  .map((s: string) => s.trim())
  .filter(Boolean);

function allowedOrigins(): Set<string> {
  return new Set([window.location.origin, ...EXTRA_ORIGINS]);
}

/** Where to send messages back to: the embedder, if we recognise it. */
function parentOrigin(): string {
  try {
    const ref = document.referrer ? new URL(document.referrer).origin : '';
    if (ref && allowedOrigins().has(ref)) return ref;
  } catch { /* fall through */ }
  return window.location.origin;
}

function post(msg: Record<string, unknown>) {
  // At top level `parent` is the window itself, which is harmless: the app
  // ignores its own messages. Not special-casing it also keeps this testable.
  window.parent.postMessage(msg, parentOrigin());
}

export type PreviewMode = 'select' | 'interact';

export function usePreviewBridge(opts: {
  tenantId: string;
  dispatch: Dispatch<Action>;
  onConfig: (c: AppConfig) => void;
}) {
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [mode, setMode] = useState<PreviewMode>('select');
  const modeRef = useRef<PreviewMode>('select');
  modeRef.current = mode;
  const { tenantId, dispatch, onConfig } = opts;

  useEffect(() => {
    if (!PREVIEW) return;
    const allowed = allowedOrigins();

    const onMessage = (e: MessageEvent) => {
      if (!allowed.has(e.origin)) return;
      const d = e.data as { type?: string; [k: string]: unknown } | null;
      if (!d || typeof d !== 'object' || typeof d.type !== 'string') return;

      switch (d.type) {
        case 'ce:config':
          // Same path as a network config: repaired, never taken on trust.
          onConfig(sanitizeConfig(d.config, tenantId).config);
          break;
        case 'ce:goto':
          if (typeof d.pageId === 'string') dispatch({ type: 'openPage', pageId: d.pageId });
          else if (typeof d.screen === 'string') dispatch({ type: 'go', screen: d.screen as never });
          break;
        case 'ce:day':
          if (typeof d.day === 'string') dispatch({ type: 'setDay', day: d.day });
          break;
        case 'ce:theme':
          if (typeof d.id === 'string') dispatch({ type: 'setTheme', id: d.id });
          break;
        case 'ce:mode':
          if (d.mode === 'select' || d.mode === 'interact') setMode(d.mode);
          break;
        case 'ce:highlight': {
          const id = typeof d.id === 'string' ? d.id : null;
          setHighlightId(id);
          if (id) {
            // Wait a frame so a just-added widget is in the DOM.
            requestAnimationFrame(() =>
              document.querySelector(`[data-wid="${CSS.escape(id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
            );
          }
          break;
        }
      }
    };

    // In select mode a click on a widget selects it in the editor instead of
    // acting. Switch to interact to try the widget for real.
    const onClick = (e: MouseEvent) => {
      if (modeRef.current !== 'select') return;
      const el = (e.target as HTMLElement | null)?.closest?.('[data-wid]');
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      post({ type: 'ce:select', id: el.getAttribute('data-wid') });
    };

    window.addEventListener('message', onMessage);
    document.addEventListener('click', onClick, true);
    post({ type: 'ce:ready' });
    return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('click', onClick, true);
    };
  }, [tenantId, dispatch, onConfig]);

  return { highlightId, mode };
}
